import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Building2, Shield } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

// Use environment variable with fallback to current origin
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const { setUser } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/admin/login`, formData, {
        withCredentials: true
      });

      if (response.data.success) {
        // Update the auth context with the user data
        setUser(response.data.user);
        
        // Store session_token in localStorage as fallback for iOS Safari
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
        
        toast.success("Login successful!");
        // Navigate to CRM
        navigate("/crm");
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error.response?.status === 401) {
        toast.error("Invalid email or password");
      } else if (error.response?.status === 403) {
        toast.error(error.response.data.detail || "Access denied");
      } else {
        toast.error("Login failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFFFF' }}>
      <header className="border-b border-slate-700 sticky top-0 z-50 shadow-lg bg-slate-900">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity duration-300">
            <Building2 className="h-8 w-8" style={{ color: '#3b82f6' }} />
            <span className="text-2xl font-bold text-white">Top Prop</span>
          </a>
          <Button
            onClick={() => navigate("/")}
            variant="outline"
            className="transition-all duration-300"
            style={{ 
              borderColor: '#FFFFFF',
              color: '#FFFFFF',
              borderRadius: '10px'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; e.currentTarget.style.borderColor = '#3b82f6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = '#FFFFFF'; }}
          >
            Return to Home
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-md mx-auto">
          <Card className="bg-white" style={{ border: '1px solid #F7F7F7', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)', borderRadius: '12px' }}>
            <CardHeader>
              <div className="flex items-center justify-center mb-4">
                <div className="h-12 w-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                  <Shield className="h-6 w-6" style={{ color: '#3b82f6' }} />
                </div>
              </div>
              <CardTitle className="text-center text-2xl" style={{ color: '#1E1E1E' }}>Admin Login</CardTitle>
              <CardDescription className="text-center" style={{ color: '#3A3A3A' }}>
                Secure access for administrators only
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" style={{ color: '#1E1E1E' }}>Admin Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="admin@topprop.ca"
                    style={{ borderColor: '#9C9C9C' }}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" style={{ color: '#1E1E1E' }}>Password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    style={{ borderColor: '#9C9C9C' }}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full text-white font-semibold transition-all duration-300"
                  style={{ 
                    backgroundColor: '#3b82f6',
                    borderRadius: '10px'
                  }}
                  onMouseEnter={(e) => {
                    if (!submitting) {
                      e.currentTarget.style.background = 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)';
                      e.currentTarget.style.color = '#000000';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#3b82f6';
                    e.currentTarget.style.color = '#FFFFFF';
                  }}
                  disabled={submitting}
                >
                  {submitting ? "Logging in..." : "Login"}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate("/admin/forgot-password")}
                  className="text-sm transition-colors duration-300"
                  style={{ color: '#3A3A3A' }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#3A3A3A'}
                >
                  Forgot your password?
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
